import React, { useEffect, useMemo, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import Loader from "@/components/Loader";
import EmptyState from "@/components/EmptyState";

export default function AdminReservations() {
  const [data, setData] = useState(null);
  const [filterAtelier, setFilterAtelier] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/admin/reservations").then(({ data }) => setData(data)).catch(() => setData({ reservations: [], ateliers: [], slots: [] }));
  }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.reservations.filter((r) =>
      (filterAtelier === "all" || r.atelierId === filterAtelier)
      && (!q || [r.firstName, r.lastName, r.email].join(" ").toLowerCase().includes(q.toLowerCase()))
    );
  }, [data, filterAtelier, q]);

  const atelierMap = useMemo(() => {
    const m = {};
    (data?.ateliers || []).forEach((a) => { m[a.id] = a.title; });
    return m;
  }, [data]);

  const slotMap = useMemo(() => {
    const m = {};
    (data?.slots || []).forEach((s) => { m[s.id] = s.label || (s.startTime ? new Date(s.startTime).toLocaleString("fr-CH") : s.id); });
    return m;
  }, [data]);

  const downloadCsv = () => {
    const token = localStorage.getItem("hsg_admin_token");
    const url = `${API_BASE}/admin/reservations.csv?token=${encodeURIComponent(token)}`;
    window.open(url, "_blank");
  };

  if (!data) return <Loader />;

  return (
    <div className="p-4 md:p-8 max-w-7xl">
      <div className="flex flex-wrap justify-between items-end gap-3 mb-4">
        <div>
          <h1 className="text-3xl font-black">Réservations</h1>
          <p className="text-neutral-400 text-sm">Total : {data.reservations.length} — Affiché : {filtered.length}</p>
        </div>
        <button onClick={downloadCsv} className="brand-gradient-btn px-4 py-2 rounded-lg" data-testid="csv-download">📄 Exporter CSV</button>
      </div>

      <div className="card-hsg p-4 mb-4 flex flex-wrap gap-3">
        <select value={filterAtelier} onChange={(e) => setFilterAtelier(e.target.value)} className="bg-neutral-900 border border-neutral-700 rounded-lg px-3 py-2 text-sm" data-testid="filter-atelier">
          <option value="all">Tous les ateliers</option>
          {data.ateliers.map((a) => <option key={a.id} value={a.id}>{a.title}</option>)}
        </select>
        <input placeholder="Recherche nom / email…" value={q} onChange={(e) => setQ(e.target.value)} className="flex-1 min-w-48 bg-neutral-900 border border-neutral-700 rounded-lg px-3 py-2 text-sm" data-testid="filter-search" />
      </div>

      {filtered.length === 0 ? <EmptyState title="Aucune réservation" /> : (
        <div className="card-hsg overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-neutral-400 border-b border-neutral-800">
                <th className="p-3">Date</th>
                <th className="p-3">Atelier</th>
                <th className="p-3">Créneau</th>
                <th className="p-3">Parent</th>
                <th className="p-3">Email</th>
                <th className="p-3">Enfants</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-b border-neutral-900 hover:bg-neutral-900/50">
                  <td className="p-3 text-neutral-400 text-xs">{r.createdAt ? new Date(r.createdAt).toLocaleString("fr-CH") : "—"}</td>
                  <td className="p-3 font-semibold">{atelierMap[r.atelierId] || r.atelierId}</td>
                  <td className="p-3">{slotMap[r.slotId] || r.slotId}</td>
                  <td className="p-3">{r.firstName} {r.lastName}</td>
                  <td className="p-3 text-neutral-400">{r.email}</td>
                  <td className="p-3">
                    <span className="chip" style={{ background: "#5BC5F222", color: "#5BC5F2", border: "1px solid #5BC5F255" }}>{r.numChildren}</span>
                    <div className="text-xs text-neutral-500 mt-1">
                      {(r.children || []).map((c, i) => <span key={i}>{c.firstName || ""} ({c.age}){i < r.children.length - 1 ? ", " : ""}</span>)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
